import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';

interface AssociatedUser {
  id: string;
  name: string;
  email: string;
}

interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string;
  description: string;
  createdAt: string;
  status: 'OPEN' | 'RESOLVED';
  user?: AssociatedUser | null;
}

export default function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadContact();
    // eslint-disable-next-line
  }, [id]);

  const loadContact = async () => {
    try {
      setLoading(true);
      const response = await authService.makeAuthenticatedRequest(`/contact/${id}`);
      if (response.ok) {
        const data = await response.json();
        setContact(data);
      } else {
        setError('Erro ao carregar contato');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar contato');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    try {
      const response = await authService.makeAuthenticatedRequest(`/contact/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'RESOLVED' }),
      });
      if (response.ok) {
        await loadContact();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Erro ao resolver contato');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao resolver contato');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja excluir esta mensagem?')) return;
    try {
      const response = await authService.makeAuthenticatedRequest(`/contact/${id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        navigate('/dashboard/contacts');
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Erro ao deletar contato');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao deletar contato');
    }
  };

  if (loading) return <div style={{ padding: 32 }}>Carregando...</div>;
  if (error) return <div style={{ padding: 32, color: 'red' }}>{error}</div>;
  if (!contact) return <div style={{ padding: 32 }}>Contato não encontrado.</div>;

  return (
    <div
      style={{
        padding: 32,
        maxWidth: 900,
        margin: '0 auto',
      }}
    >
      <h1
        style={{
          fontSize: 28,
          fontWeight: 700,
          marginBottom: 24,
          color: 'var(--card-foreground)',
        }}
      >
        Detalhes da Mensagem
      </h1>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 24,
          background: 'var(--muted)',
          padding: 20,
          borderRadius: 12,
          marginBottom: 24,
        }}
      >
        <div>
          <p
            style={{
              fontSize: 13,
              textTransform: 'uppercase',
              color: 'var(--muted-foreground)',
              marginBottom: 4,
            }}
          >
            Nome
          </p>
          <p style={{ fontSize: 16, fontWeight: 500 }}>{contact.name}</p>
        </div>
        <div>
          <p
            style={{
              fontSize: 13,
              textTransform: 'uppercase',
              color: 'var(--muted-foreground)',
              marginBottom: 4,
            }}
          >
            E-mail
          </p>
          <p style={{ fontSize: 16, fontWeight: 500 }}>{contact.email}</p>
        </div>
        <div>
          <p
            style={{
              fontSize: 13,
              textTransform: 'uppercase',
              color: 'var(--muted-foreground)',
              marginBottom: 4,
            }}
          >
            Telefone
          </p>
          <p style={{ fontSize: 16, fontWeight: 500 }}>{contact.phone || '-'}</p>
        </div>
        <div>
          <p
            style={{
              fontSize: 13,
              textTransform: 'uppercase',
              color: 'var(--muted-foreground)',
              marginBottom: 4,
            }}
          >
            Enviado em
          </p>
          <p style={{ fontSize: 16, fontWeight: 500 }}>
            {new Date(contact.createdAt).toLocaleString('pt-BR')}
          </p>
        </div>
      </div>

      {contact.user && (
        <div style={{ marginBottom: 24 }}>
          <p
            style={{
              fontSize: 13,
              textTransform: 'uppercase',
              color: 'var(--muted-foreground)',
              marginBottom: 8,
            }}
          >
            Usuário Associado
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              background: 'white',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 12,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'var(--muted)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                color: 'var(--card-foreground)',
              }}
            >
              {contact.user.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div
                style={{
                  fontWeight: 600,
                  color: 'var(--card-foreground)',
                  fontSize: 15,
                }}
              >
                {contact.user.name}
              </div>
              <div style={{ color: 'var(--muted-foreground)', fontSize: 14 }}>
                {contact.user.email}
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <p
          style={{
            fontSize: 13,
            textTransform: 'uppercase',
            color: 'var(--muted-foreground)',
            marginBottom: 4,
          }}
        >
          Mensagem
        </p>
        <div
          style={{
            background: 'var(--muted)',
            padding: 16,
            borderRadius: 8,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            fontSize: 15,
            lineHeight: 1.5,
          }}
        >
          {contact.description}
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <p
          style={{
            fontSize: 13,
            textTransform: 'uppercase',
            color: 'var(--muted-foreground)',
            marginBottom: 4,
          }}
        >
          Status
        </p>
        <span
          style={{
            display: 'inline-block',
            padding: '6px 12px',
            borderRadius: 20,
            fontSize: 14,
            fontWeight: 600,
            background:
              contact.status === 'RESOLVED' ? 'var(--accent)' : 'var(--muted)',
            color:
              contact.status === 'RESOLVED'
                ? 'var(--accent-foreground)'
                : 'var(--card-foreground)',
          }}
        >
          {contact.status === 'RESOLVED' ? 'Resolvido' : 'Aberto'}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        {contact.status === 'OPEN' && (
          <button
            onClick={handleResolve}
            style={{
              background: 'var(--accent)',
              color: 'var(--accent-foreground)',
              border: 'none',
              padding: '10px 18px',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Marcar como Resolvido
          </button>
        )}
        <button
          onClick={handleDelete}
          style={{
            background: 'var(--destructive)',
            color: 'white',
            border: 'none',
            padding: '10px 18px',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Excluir
        </button>
      </div>
    </div>
  );
}
